import React, {
  useState,
  useMemo,
  useEffect,
  useImperativeHandle,
  useRef,
  useActionState,
  startTransition
} from 'react'
import { Modal, ModalBody, ModalHeader, Spinner, UncontrolledTooltip } from 'reactstrap'
import Skeleton from 'react-loading-skeleton'
// @ts-ignore
import 'react-loading-skeleton/dist/skeleton.css'
//@ts-ignore
import { Button, Badge, Avatar, InputGroup } from 'dsm-react-lib'
import DataTable from 'react-data-table-component'
import { useSelector } from 'react-redux'
import {
  IconArrowLeft,
  IconAlertTriangle,
  IconUsers,
  IconX,
  IconSearch,
  IconChevronRight,
  IconPackage,
  IconBox
} from '@tabler/icons-react'
import { useDataboxService } from '../services/databoxService'
import { toast } from 'react-toastify'
import SelectedSourcesPanel from './SelectedSourcesPanel'
import DataboxDataGrid from '../DataboxDataGrid'
import { useTeamService } from '../services/teamService'
import { useSasToken } from '../../../../_hooks/useSasToken'
import { getInitials } from '../../../../utility/Utils'
import { PendingIcon } from '../../Chatbot/icons/Icons'
import { isLockedItem } from './lockUtils'
import { useNavigate } from 'react-router-dom'
import { useThemeManager } from '../../../../_hooks/useThemeManager'

// React 19 skips the library's default callbacks; client-side pagination needs no external update.
const handleClientPaginationChange = () => undefined

interface AddDataboxModalProps {
  isOpen: boolean
  toggle: () => void
  // Shared tab bar, rendered as the first item of this tab's own left column so the right-hand
  // Selected Sources panel/divider (a sibling of that left column) starts level with the tabs.
  tabsBar?: React.ReactNode
  existingDataboxes?: any[]
  addDataboxToAgent?: (databoxes: any) => void
  removeDataboxFromAgent?: (databoxIds: string[]) => void
  initialDataboxId?: string | null
  // Raw teamIdentifier from the agent's SmartInsights scope config
  smartInsightsTeamIdentifier?: string
  // When true, renders just the body content (no outer Modal/ModalHeader chrome) so this can be
  // embedded as a tab inside a shared modal (see DataSourcesModal.tsx)
  asTabContent?: boolean
  // Standalone data products already selected on the Data Products tab (live - reflects any
  // not-yet-submitted removal made from this tab's own panel)
  standaloneDataProducts?: Array<{ id: string; name: string }>
  onRemoveDataProduct?: (id: string) => void
  // Whether the agent had ANY data products persisted before this modal opened - unlike the live standaloneDataProducts, doesn't drop to 0 the moment the last one is unchecked, so the submit button stays enabled to persist that removal.
  hasExistingDataProducts?: boolean
  // The Personal Databox tab's current (unsaved) selection
  existingPersonalDataboxForPanel?: any[]
  onRemovePersonalDatabox?: () => void
  onRemovePersonalFile?: (fileId: string) => void
  lockedDataboxIds?: string[]
  lockedItemIds?: string[]
  lockedDataProductIds?: string[]
  // Reports this tab's current (unsaved) selection to the parent modal
  onLiveSelectionChange?: (databoxes: any[]) => void
  // Lets the other tab commit its own pending selection before this tab submits
  onBeforeSubmit?: () => void
  onTeamsAvailabilityChange?: (hasTeams: boolean) => void
  // Reports whether this tab's own databox list/details fetch is still in flight
  onLoadingChange?: (isLoading: boolean) => void
  // Loading state of the OTHER two categories (this tab's own Team state is computed locally)
  isFetchingPersonal?: boolean
  isFetchingDataProducts?: boolean
  // True while the agent's existing data sources are still being fetched from the server -
  // shows a loading state in the Selected Sources panel instead of "No sources selected"
  isLoadingExistingSources?: boolean
  // True (default) in the Agent Wizard where Redux's agentReducer.agent is reliable; false in chat's "Add sources" flow where it may hold stale leftover state and would hijack team resolution.
  useAgentWizardTeamContext?: boolean
  // True when the chat's currently selected agent (selectedAgent.agentType === 'Team') is a Team
  isTeamAgent?: boolean
  // The chat agent's own scopeId (team GUID) - the chat-flow equivalent of agent.scopeId used by
  // the Agent Wizard's Redux state.
  agentScopeId?: string
  // True (Agent Wizard) / passed explicitly (chat flow) when the agent's visibility is Enterprise -
  // restricts the team list to the Community team only and hides the single-team warning banner.
  isEnterpriseAgent?: boolean
  // Ref exposing this tab's imperative handle to the parent DataSourcesModal
  ref?: React.Ref<AddDataboxModalHandle>
}

interface User {
  id: string
  sid: string
  displayName: string
  email: string
  jobTitle: string
  role: string
  status: string
  profilePictureBase64Encoded: string
}

export interface Databox {
  id: string
  smartDocsDataboxId?: string
  name: string
  description: string | null
  databoxDocumentsCount?: number
  databoxDataProductsCount?: number
  filesCount?: number
  dataProductsCount?: number
  createdBy: User
  createdAt: string
  modifiedAt?: string
  modifiedBy?: User
  lastModifiedAt?: string
  lastModifiedBy?: User
  dataProductsMetadataSources: any[]
  totalDocumentsSize: number
  type: string
  status: string
  teamIdentifier?: string
  scopeId?: string
}

const formatDate = (dateString: string): string => {
  return dateString ? new Date(dateString).toDateString().slice(3) : '-'
}

// Stable no-op reference for DataboxDataGrid's unused callback props - passing a fresh inline
// `() => {}` on every render would give it new prop references each time, defeating its
const noopHandler = () => {}

// Imperative methods exposed to the parent DataSourcesModal
export interface AddDataboxModalHandle {
  uncheckDatabox: (id: string) => void
  // Removes a single file/data product from a databox's narrowed selection
  removeItem: (databoxId: string, itemId: string) => void
  // Commits this tab's selection without closing the modal
  commitPendingSelection: () => void
}

// ** Bootstrap Checkbox Component
// Defined at module scope (not inside AddDataboxModal) so its identity is stable across
// renders
interface BootstrapCheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  // Whether this databox has only some of its files/data products selected (not the whole
  // databox) - shows an indeterminate (partial) checkbox instead of a full checkmark
  isPartiallySelected?: boolean
  // Class applied to the wrapping .custom-control div (not the input)
  wrapperClassName?: string
  // Whether the checkbox is disabled
  disabled?: boolean
  ref?: React.Ref<HTMLInputElement>
}

const BootstrapCheckbox = ({
  onClick,
  name,
  isPartiallySelected,
  wrapperClassName,
  disabled,
  ref,
  ...rest
}: BootstrapCheckboxProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = !!isPartiallySelected
    }
  }, [isPartiallySelected])

  return (
    <div className={`custom-control custom-checkbox ${wrapperClassName || ''} !pl-0`}>
      <input
        type="checkbox"
        className="custom-control-input"
        disabled={disabled}
        name={name}
        onClick={onClick}
        ref={(node) => {
          inputRef.current = node
          if (typeof ref === 'function') ref(node)
          else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node
        }}
        {...rest}
      />
    </div>
  )
}

const AddDataboxModal = ({
  isOpen,
  toggle,
  tabsBar,
  existingDataboxes = [],
  addDataboxToAgent,
  removeDataboxFromAgent,
  initialDataboxId = null,
  smartInsightsTeamIdentifier,
  asTabContent = false,
  standaloneDataProducts = [],
  hasExistingDataProducts = false,
  onRemoveDataProduct,
  existingPersonalDataboxForPanel = [],
  onRemovePersonalDatabox,
  onRemovePersonalFile,
  lockedDataboxIds = [],
  lockedItemIds = [],
  lockedDataProductIds = [],
  onLiveSelectionChange,
  onBeforeSubmit,
  onTeamsAvailabilityChange,
  onLoadingChange,
  isFetchingPersonal,
  isFetchingDataProducts,
  isLoadingExistingSources = false,
  useAgentWizardTeamContext = true,
  isTeamAgent = false,
  agentScopeId,
  isEnterpriseAgent = false,
  ref
}: AddDataboxModalProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [databoxDetailsCache, setDataboxDetailsCache] = useState<{ [key: string]: any }>({})
  const [clearSelectedRows, setClearSelectedRows] = useState(false)
  const [dataTableKey, setDataTableKey] = useState(0)
  const [databoxes, setDataboxes] = useState<Databox[]>([])
  const {
    isGettingDataboxList,
    getDataboxes,
    getDataboxDetails,
    getDataboxInfo,
    getDocumentDetails,
    getDataProduct
  } = useDataboxService()
  const [showSpinner, setShowSpinner] = useState(false)
  const [isResolvingSource, setIsResolvingSource] = useState(false)
  const [browsingDataboxId, setBrowsingDataboxId] = useState<string | null>(null)
  const [smartDocsDataboxId, setSmartDocsDataboxId] = useState<string | null>(null)
  const [isFetchingDetails, setIsFetchingDetails] = useState(false)
  const [loadingBrowseId, setLoadingBrowseId] = useState<string | null>(null)
  const [databoxDetails, setDataboxDetails] = useState<any>(null)
  const [databoxFiles, setDataboxFiles] = useState<any[]>([])
  const [databoxDataProducts, setDataboxDataProducts] = useState<any[]>([])
  const [selectedDocumentsByDatabox, setSelectedDocumentsByDatabox] = useState<{
    [databoxId: string]: {
      name: string
      items: any[]
      customSelection?: any
      databoxId?: string
    }
  }>({})
  const { isFetchingTeams, getAllTeams, getTeamDetailsById } = useTeamService()
  const [teamsData, setTeamsData] = useState<any[]>([])
  const { appendToken } = useSasToken()
  const navigate = useNavigate()
  const [selectedTeam, setSelectedTeam] = useState<any>(null)
  // Last team actually browsed - unlike selectedTeam, not cleared by "Back to Teams"
  const lastBrowsedTeamIdRef = useRef<string | null>(null)
  // Ensures the existing-databoxes pre-select effect below only runs once per modal-open
  const hasInitializedExistingSelectionRef = useRef(false)
  // Mirrors the ref above but as state, so the live-selection report effect can wait for it
  const [hasInitializedExistingSelection, setHasInitializedExistingSelection] = useState(false)
  // Tracks removals requested cross-tab before the pre-select/reconstruction effect finishes, so it can't be silently reverted by that effect's overwrite.
  const pendingRemovedDataboxIdsRef = useRef<Set<string>>(new Set())
  const pendingRemovedItemIdsRef = useRef<Map<string, Set<string>>>(new Map())
  // True once the user has explicitly removed a databox/item - lets the live-selection report effect fire even if hasInitializedExistingSelection never becomes true.
  const [hasPendingRemoval, setHasPendingRemoval] = useState(false)
  const { isDarkTheme } = useThemeManager()
  const agent = useSelector((state: any) => state?.agentReducer?.agent)
  // True when team selection must be locked to a single team
  const isTeamLocked = useAgentWizardTeamContext
    ? agent?.agentType === 'Team' && !!agent?.scopeId
    : isTeamAgent
  // Enterprise agents only ever deal with the Community team's databoxes
  const isEnterpriseTypeAgent = useAgentWizardTeamContext
    ? agent?.agentType === 'Enterprise'
    : isEnterpriseAgent
  const lockedDataboxIdSet = useMemo(
    () => new Set(lockedDataboxIds.map(String)),
    [lockedDataboxIds]
  )
  const lockedItemIdSet = useMemo(() => new Set(lockedItemIds.map(String)), [lockedItemIds])
  const lockedDataProductIdSet = useMemo(
    () => new Set(lockedDataProductIds.map(String)),
    [lockedDataProductIds]
  )
  const browsingLockedDataboxIds = useMemo(() => {
    const ids = new Set<string>(lockedDataboxIds.map(String))
    existingDataboxes.forEach((db: any) => {
      if ((db?.selectedItems?.length ?? 0) > 0) {
        const databoxId = db.smartDocsDataboxId || db.id
        if (databoxId) {
          ids.add(String(databoxId))
        }
      }
    })
    return Array.from(ids)
  }, [existingDataboxes, lockedDataboxIds])

  // Keep the ref in sync with selectedTeam, but only when truthy
  useEffect(() => {
    if (selectedTeam?.id) {
      lastBrowsedTeamIdRef.current = selectedTeam.id
    }
  }, [selectedTeam])

  // Report this tab's current (not-yet-saved) databox selection to the parent on every change,
  // so the shared modal can show it live in the Data Products tab's panel too.
  useEffect(() => {
    if (!onLiveSelectionChange) return
    // Don't report yet if there are existing databoxes to restore but the pre-select effect
    // hasn't reconstructed them - UNLESS the user has explicitly removed something, which must
    // always be reported regardless.
    if (existingDataboxIds.length > 0 && !hasInitializedExistingSelection && !hasPendingRemoval) {
      return
    }

    const allIds = Array.from(
      new Set([...selectedRows, ...Object.keys(selectedDocumentsByDatabox)])
    )
    const live = allIds.map((id) => {
      const databox = databoxes.find(
        (db) =>
          String(db.id) === String(id) || String((db as any).smartDocsDataboxId) === String(id)
      )
      const entry = selectedDocumentsByDatabox[id]
      return {
        ...databox,
        id: databox?.id || id,
        smartDocsDataboxId: databox?.smartDocsDataboxId || id,
        name: entry?.name || databox?.name || id,
        ...(entry?.items?.length > 0 && { selectedItems: entry.items })
      }
    })
    onLiveSelectionChange(live)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedRows,
    selectedDocumentsByDatabox,
    databoxes,
    hasInitializedExistingSelection,
    hasPendingRemoval
  ])

  // Helper: Reconstruct full objects from IDs
  const reconstructItemsFromIds = async (
    cognitiveSearchIds: string[],
    dataProductsIds: string[]
  ): Promise<any[]> => {
    const items: any[] = []

    // Fetch documents
    if (cognitiveSearchIds.length > 0) {
      const docs = await getDocumentDetails(cognitiveSearchIds).catch(() => [])
      if (Array.isArray(docs)) {
        docs.forEach((doc: any, index: number) => {
          items.push({
            id: doc.externalId || doc.id,
            name: doc.name,
            type: 'file',
            smartDocsDocumentId: doc.id,
            // Positional fallback if the API response doesn't include cognitiveSearchId directly
            cognitiveSearchId: doc.cognitiveSearchId || cognitiveSearchIds[index],
            owner: doc.uploadedBy || '-',
            dateAdded: doc.uploadedDate,
            status: doc.status,
            tags: doc.tags || []
          })
        })
      }
    }

    // Fetch data products
    if (dataProductsIds.length > 0) {
      const products = await Promise.all(
        dataProductsIds.map((id: string) => getDataProduct(id).catch(() => null))
      )
      products.forEach((dp: any, index: number) => {
        if (dp) {
          const originalId = dataProductsIds[index]
          items.push({
            id:
              dp.dataProductExternalId || dp.associationId || dp.externalId || dp.id || originalId,
            // Real product id, kept separate from the display id above so it stays correct
            // if this selection is saved again without re-checking it
            dataProductId: dp.dataProductExternalId || dp.id || originalId,
            // Fall back to the fetched id (not '-') when the API doesn't return a name
            name: dp.name || originalId,
            type: 'dataproduct',
            owner: dp.owners?.[0] || '-',
            dateAdded: dp.associatedAt,
            tags: dp.tags || [],
            status: dp.isDiscoverable ? 'active' : 'pending',
            tier: dp.tier || 'Source'
          })
        }
      })
    }

    return items
  }

  // Get IDs of databoxes already associated with the agent
  const existingDataboxIds = useMemo(() => {
    const ids = new Set<string>()
    existingDataboxes.forEach((db: any) => {
      if (db.id) ids.add(String(db.id))
      if (db.smartDocsDataboxId) ids.add(String(db.smartDocsDataboxId))
    })
    return Array.from(ids)
  }, [existingDataboxes])

  // Reports this tab's own fetch state (loading the team databox list + selected-databox
  // details) to the parent modal, so it can be combined with the other tabs' loading state.
  useEffect(() => {
    onLoadingChange?.(
      isFetchingDetails || (existingDataboxIds.length > 0 && !hasInitializedExistingSelection)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingDetails, existingDataboxIds, hasInitializedExistingSelection])

  // Pre-select existing databoxes once per modal-open (not on every team switch)
  useEffect(() => {
    if (isOpen && databoxes.length > 0) {
      if (hasInitializedExistingSelectionRef.current) return
      hasInitializedExistingSelectionRef.current = true

      if (existingDataboxIds.length > 0) {
        const wholeDataboxIds: string[] = []
        const customSelectionsToReconstruct: Array<{
          key: string
          name: string
          cognitiveSearchIds: string[]
          dataProductsIds: string[]
        }> = []

        existingDataboxes.forEach((db: any) => {
          const idToUse = db.smartDocsDataboxId ? String(db.smartDocsDataboxId) : String(db.id)

          // Skip entirely if this databox was already removed (e.g. via "Remove all" clicked
          // from the Personal Databox tab's shared panel) while this effect was still pending -
          // otherwise it would be silently re-added here, reverting that removal.
          if (pendingRemovedDataboxIdsRef.current.has(idToUse)) return

          // Check for selectedItems array (current UI state)
          if (db.selectedItems && Array.isArray(db.selectedItems) && db.selectedItems.length > 0) {
            // Extract cognitive search IDs from selectedItems
            const cognitiveSearchIds = [
              ...new Set<string>(
                db.selectedItems
                  .filter((item: any) => item.cognitiveSearchId && item.type === 'file')
                  .map((item: any) => item.cognitiveSearchId)
              )
            ]

            const dataProductIds = [
              ...new Set<string>(
                db.selectedItems
                  .filter((item: any) => item.type === 'dataproduct' && item.id)
                  .map((item: any) => String(item.dataProductId ?? item.id))
              )
            ]

            if (cognitiveSearchIds.length > 0 || dataProductIds.length > 0) {
              customSelectionsToReconstruct.push({
                key: idToUse,
                name: db.name || '',
                cognitiveSearchIds,
                dataProductsIds: dataProductIds
              })
            }
          } else {
            // Whole databox (no item-level selection)
            if (!wholeDataboxIds.includes(idToUse)) {
              wholeDataboxIds.push(idToUse)
            }
          }
        })

        // Set selected rows for whole databoxes, unless the user already selected something while this was resolving.
        setSelectedRows((prev) => (prev.length > 0 ? prev : wholeDataboxIds))

        // Clear old document selections before reconstructing, unless the user already made one while this was pending.
        setSelectedDocumentsByDatabox((prev) => (Object.keys(prev).length > 0 ? prev : {}))

        // Reconstruct all custom selections from IDs
        if (customSelectionsToReconstruct.length > 0) {
          Promise.all(
            customSelectionsToReconstruct.map(
              async ({ key, name, cognitiveSearchIds, dataProductsIds }) => {
                const items = await reconstructItemsFromIds(cognitiveSearchIds, dataProductsIds)
                return { key, name, items }
              }
            )
          ).then((results) => {
            const selections: any = {}
            results.forEach(({ key, name, items }) => {
              // Re-apply any item removals that landed while this reconstruction was in flight, so they aren't silently reverted by this overwrite.
              const removedItemIds = pendingRemovedItemIdsRef.current.get(key)
              const filteredItems = removedItemIds
                ? items.filter((item: any) => !removedItemIds.has(item.id))
                : items
              if (filteredItems.length > 0) {
                selections[key] = { name, items: filteredItems }
              }
            })
            // Don't clobber a selection the user already made while this was resolving.
            setSelectedDocumentsByDatabox((prev) =>
              Object.keys(prev).length > 0 ? prev : selections
            )
            setDataTableKey((k) => k + 1)
            // Only now (once custom selections are fully reconstructed) is the live
            // selection reported downstream
            setHasInitializedExistingSelection(true)
          })
        } else {
          setDataTableKey((k) => k + 1)
          setHasInitializedExistingSelection(true)
        }
      } else {
        // No existing databoxes - clear all selections, unless the user already made one
        // while this one-time init effect was still pending (async databoxes fetch racing
        // a fast interaction), which would otherwise silently wipe it out.
        // Note: no setDataTableKey bump here - there's nothing to reconstruct into the table,
        // and remounting the DataTable this early (right as its checkboxes first become
        // interactive) can orphan a DOM node a caller already grabbed a reference to.
        setSelectedRows((prev) => (prev.length > 0 ? prev : []))
        setSelectedDocumentsByDatabox((prev) => (Object.keys(prev).length > 0 ? prev : {}))
        setHasInitializedExistingSelection(true)
      }
    }
  }, [isOpen, databoxes.length, existingDataboxIds])

  // Reset states when modal closes and load teams/databoxes on open
  useEffect(() => {
    if (!isOpen) {
      // Reset ALL state to ensure clean slate for next open
      setSelectedRows([])
      setSelectedDocumentsByDatabox({})
      setBrowsingDataboxId(null)
      setSearchTerm('')
      setClearSelectedRows(false)
      setDataTableKey((k) => k + 1)
      setSelectedTeam(null)
      setDataboxes([])
      setDataboxDetailsCache({})
      setDataboxDetails(null)
      setDataboxFiles([])
      setDataboxDataProducts([])
      setTeamsData([])
      setIsFetchingDetails(false)
      setLoadingBrowseId(null)
      setIsResolvingSource(false)
      lastBrowsedTeamIdRef.current = null
      hasInitializedExistingSelectionRef.current = false
      setHasInitializedExistingSelection(false)
      pendingRemovedDataboxIdsRef.current = new Set()
      pendingRemovedItemIdsRef.current = new Map()
      return
    }

    // Skip loading teams/databoxes if in edit mode (initialDataboxId is set)
    if (initialDataboxId) {
      return
    }

    const loadDataboxesOrTeams = async () => {
      setIsResolvingSource(true)
      try {
        // Always fetch the teams list first
        let activeTeams: any[] = []
        try {
          const response = await getAllTeams(0, 100)
          activeTeams = (response?.items || []).filter(
            (team: any) =>
              (team.userRole !== null || team.name === 'Community') && team.status === 'Active'
          )
          if (isEnterpriseTypeAgent) {
            activeTeams = activeTeams.filter((team: any) => team.name === 'Community')
          }
        } catch (error) {
          console.error('Error fetching teams:', error)
        }
        setTeamsData(activeTeams)

        // Only Team Databoxes tab entries carry real team scope info - the Personal Databox
        // tab's entry can carry its own scopeId/teamId (e.g. an empty/placeholder GUID) which
        // must never be mistaken for a team identifier here.
        const teamOnlyDataboxes = (existingDataboxes || []).filter(
          (db: any) => db.scope !== 'Personal'
        )

        // Check if there's a scopeId or teamIdentifier (team GUID) in existing databoxes
        // Prioritize scopeId (GUID), then teamIdentifier (might be GUID or might be old numeric ID)
        let existingTeamId =
          teamOnlyDataboxes.find((db: any) => db.scopeId)?.scopeId ||
          teamOnlyDataboxes.find((db: any) => {
            // Only use teamIdentifier if it's a GUID (contains dashes), not a numeric ID
            const id = db.teamIdentifier
            return id && typeof id === 'string' && id.includes('-')
          })?.teamIdentifier

        // Also extract smartDocsTeamId (numeric team ID) from existing databoxes
        let existingSmartDocsTeamId = teamOnlyDataboxes[0]?.teamId

        // Fallback: check if teamIdentifier contains a numeric ID (backward compatibility)
        if (!existingSmartDocsTeamId) {
          const teamIdentifierValue = teamOnlyDataboxes[0]?.teamIdentifier
          const isNumericId =
            teamIdentifierValue &&
            typeof teamIdentifierValue === 'string' &&
            !teamIdentifierValue.includes('-')
          if (isNumericId) {
            existingSmartDocsTeamId = teamIdentifierValue
          }
        }

        // If no GUID found but we have a smartDocsDataboxId, we can fetch the databox details to get team info
        if (!existingTeamId && teamOnlyDataboxes.length > 0) {
          const firstDatabox = teamOnlyDataboxes[0]
          if (firstDatabox.smartDocsDataboxId || firstDatabox.id) {
            try {
              const dbDetails = await getDataboxInfo(
                firstDatabox.smartDocsDataboxId || firstDatabox.id,
                true
              )
              // Extract team GUID and smartDocsTeamId from databox details
              existingTeamId = dbDetails?.scopeId // GUID for API calls
              existingSmartDocsTeamId = dbDetails?.teamId || dbDetails?.team?.smartDocsTeamId // Numeric ID for payload
            } catch (error) {
              console.error('Failed to fetch databox details for team resolution:', error)
            }
          }
        }

        // Resolve a team's real name/smartDocsTeamId from the teams list we just fetched,
        // comparing both the GUID id and the numeric smartDocsTeamId
        const findTeamMatch = (teamId?: string | null, smartDocsTeamId?: string | null) =>
          activeTeams.find(
            (team: any) =>
              (teamId && String(team.id) === String(teamId)) ||
              (smartDocsTeamId && String(team.smartDocsTeamId) === String(smartDocsTeamId))
          )

        // Enterprise agents skip the team list entirely and go straight into Community's databoxes
        const communityTeam = isEnterpriseTypeAgent
          ? activeTeams.find((team: any) => team.name === 'Community')
          : null

        // If Team agent, directly load its databoxes - trusts the Agent Wizard's Redux agent
        // state when embedded in that wizard, or the chat-flow isTeamAgent/agentScopeId props
        // when embedded in chat's "Add sources" flow (see useAgentWizardTeamContext doc).
        const lockedTeamScopeId = communityTeam
          ? communityTeam.id
          : useAgentWizardTeamContext && agent?.agentType === 'Team'
          ? agent?.scopeId
          : agentScopeId
        if ((isTeamLocked || isEnterpriseTypeAgent) && lockedTeamScopeId) {
          // Resolve the numeric smartDocsTeamId via the team details endpoint when it wasn't
          // already found on the existing databoxes
          if (!existingSmartDocsTeamId) {
            try {
              const teamDetails = await getTeamDetailsById(lockedTeamScopeId)
              existingSmartDocsTeamId = teamDetails?.smartDocsTeamId
            } catch (error) {
              console.error('Failed to fetch team details for smartDocsTeamId resolution:', error)
            }
          }
          const matchedTeam =
            communityTeam ?? findTeamMatch(lockedTeamScopeId, existingSmartDocsTeamId)
          // Prefer the matched team's own GUID - findTeamMatch can match via smartDocsTeamId alone, in which case lockedTeamScopeId itself may be wrong.
          const resolvedTeamId = matchedTeam?.id ?? lockedTeamScopeId
          setSelectedTeam({
            id: resolvedTeamId,
            smartDocsTeamId: matchedTeam?.smartDocsTeamId ?? existingSmartDocsTeamId,
            name: matchedTeam?.name || agent?.name || 'Team'
          })
          onTeamsAvailabilityChange?.(true)
          setShowSpinner(true)
          await getTeamDataboxes(resolvedTeamId, existingSmartDocsTeamId)
          setShowSpinner(false)
        } else if (existingTeamId) {
          if (!existingSmartDocsTeamId) {
            try {
              const teamDetails = await getTeamDetailsById(existingTeamId)
              existingSmartDocsTeamId = teamDetails?.smartDocsTeamId
            } catch (error) {
              console.error('Failed to fetch team details for smartDocsTeamId resolution:', error)
            }
          }
          const matchedTeam = findTeamMatch(existingTeamId, existingSmartDocsTeamId)
          // Same as above - prefer the matched team's own GUID over the raw existingTeamId,
          const resolvedTeamId = matchedTeam?.id ?? existingTeamId
          // If there's a valid scopeId/teamIdentifier in existing databoxes, load that team's databoxes
          // Set selectedTeam FIRST to prevent showing teams view during loading
          setSelectedTeam({
            id: resolvedTeamId,
            smartDocsTeamId: matchedTeam?.smartDocsTeamId ?? existingSmartDocsTeamId,
            name: matchedTeam?.name || 'Team'
          })
          onTeamsAvailabilityChange?.(true)
          setShowSpinner(true)
          await getTeamDataboxes(resolvedTeamId, existingSmartDocsTeamId)
          setShowSpinner(false)
        } else {
          // Otherwise, show the teams list - unless the agent's SmartInsights scope config
          // already names a team, in which case jump straight to that team's databoxes.
          const matchedTeam = smartInsightsTeamIdentifier
            ? activeTeams.find(
                (team: any) =>
                  String(team.smartDocsTeamId) === String(smartInsightsTeamIdentifier) ||
                  String(team.id) === String(smartInsightsTeamIdentifier)
              )
            : null

          if (matchedTeam) {
            setSelectedTeam({
              id: matchedTeam.id,
              smartDocsTeamId: matchedTeam.smartDocsTeamId,
              name: matchedTeam.name
            })
            onTeamsAvailabilityChange?.(true)
            setShowSpinner(true)
            await getTeamDataboxes(matchedTeam.id, matchedTeam.smartDocsTeamId)
            setShowSpinner(false)
          } else {
            onTeamsAvailabilityChange?.(activeTeams.length > 0)
          }
        }
      } finally {
        setIsResolvingSource(false)
      }
    }

    loadDataboxesOrTeams()
  }, [
    isOpen,
    agent,
    existingDataboxes,
    initialDataboxId,
    smartInsightsTeamIdentifier,
    useAgentWizardTeamContext,
    isTeamLocked,
    isEnterpriseTypeAgent,
    agentScopeId
  ])

  // Check if a databox is already added to the agent
  const isDataboxAlreadyAdded = (row: Databox) => {
    return existingDataboxIds.some((id) => String(id) === String(row.id))
  }

  // const fetchNewDataboxDetails = async (selectedRows: string[]) => {
  //   const newDataboxIds = selectedRows.filter((id) => !databoxDetailsCache[id])
  //   setIsFetchingDetails(true)
  //   for (const databoxId of newDataboxIds) {
  //     try {
  //       const details = await getDataboxDetails(databoxId)
  //       const { dataProducts, files } = details
  //       setDataboxDetailsCache((prev) => ({ ...prev, [databoxId]: { dataProducts, files } }))
  //     } catch (error) {
  //       console.error('Error fetching details for databox ID:', databoxId, error)
  //     }
  //   }
  //   setIsFetchingDetails(false)
  // }

  // Handle browse button click
  const handleBrowseDatabox = async (row: Databox) => {
    setLoadingBrowseId(row.id)
    const response = await getDataboxDetails(row.id)
    if (response) {
      setBrowsingDataboxId(row.id)
      setSmartDocsDataboxId(response?.smartDocsDataboxId || null)
      setDataboxDetails(response)
      // setDataboxFiles(response.files || [])
      // setDataboxDataProducts(response.dataProducts || [])
      // Cache the details so counts are available in the right panel
      setDataboxDetailsCache((prev) => ({
        ...prev,
        [row.id]: {
          files: response.files || [],
          dataProducts: response.dataProducts || [],
          filesCount: response.filesCount ?? response.files?.length ?? 0,
          dataProductsCount: response.dataProductsCount ?? response.dataProducts?.length ?? 0
        }
      }))
    } else {
      toast.error('Failed to fetch databox details')
    }
    setLoadingBrowseId(null)
  }

  // Handle selection in browse mode
  // const handleBrowseSelection = (selectedItems: any[]) => {
  //   if (browsingDataboxId) {
  //     const databoxName = databoxes.find((db) => db.id === browsingDataboxId)?.name ?? ''
  //     setSelectedDocumentsByDatabox((prev) => ({
  //       ...prev,
  //       [browsingDataboxId]: { name: databoxName, items: selectedItems }
  //     }))
  //   }
  // }

  // Filter databoxes based on search term
  const filteredDataboxes = useMemo(() => {
    if (!searchTerm) return databoxes
    const searchLower = searchTerm.toLowerCase()
    return databoxes.filter((databox) => databox.name.toLowerCase().includes(searchLower))
  }, [searchTerm, databoxes])

  // Filter teams based on search term - memoized so the `data` prop passed to DataTable stays referentially stable
  const filteredTeams = useMemo(() => {
    if (!searchTerm) return teamsData
    const searchLower = searchTerm.toLowerCase()
    return teamsData.filter((team) => team.name.toLowerCase().includes(searchLower))
  }, [searchTerm, teamsData])

  // Remove selected databox
  const removeSelectedDatabox = (id: string) => {
    setSelectedRows((prev) => prev.filter((rowId) => rowId !== id))
  }

  // Clear all selections - only clear enabled checkboxes, keep disabled ones as they are
  const clearSelection = () => {
    // Keep only the disabled/already added databoxes in selection
    const disabledDataboxIds = existingDataboxIds.map((id) => String(id)).filter(Boolean)
    setSelectedRows(
      selectedRows.filter((id) => disabledDataboxIds.includes(id) || lockedDataboxIdSet.has(id))
    )
    setSelectedDocumentsByDatabox((prev) => {
      const next: typeof prev = {}
      Object.entries(prev).forEach(([key, value]) => {
        const preservedItems = (value.items || []).filter((item: any) =>
          isLockedItem(item, lockedItemIdSet)
        )
        if (preservedItems.length > 0) {
          next[key] = { ...value, items: preservedItems }
        }
      })
      return next
    })
    // Toggle clearSelectedRows to force DataTable to clear its internal selection state
    setClearSelectedRows(!clearSelectedRows)
  }

  // Remove a specific item from a databox's selection
  const handleRemoveItem = (databoxId: string, itemId: string) => {
    const itemToRemove = (selectedDocumentsByDatabox[databoxId]?.items || []).find(
      (item: any) => String(item?.id) === String(itemId)
    )
    if (lockedItemIdSet.has(String(itemId)) || isLockedItem(itemToRemove, lockedItemIdSet)) {
      return
    }
    // Recorded so the pre-select/reconstruction effect can re-apply this removal if it's still
    // in flight (or runs again) after this call.
    const pendingForDatabox = pendingRemovedItemIdsRef.current.get(databoxId) ?? new Set<string>()
    pendingForDatabox.add(itemId)
    pendingRemovedItemIdsRef.current.set(databoxId, pendingForDatabox)
    setHasPendingRemoval(true)

    setSelectedDocumentsByDatabox((prev) => {
      const entry = prev[databoxId]
      if (!entry) return prev
      const newItems = entry.items.filter((item) => item.id !== itemId)
      if (newItems.length === 0) {
        const { [databoxId]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [databoxId]: { ...entry, items: newItems } }
    })
  }

  // Remove entire databox from selections
  const handleRemoveDatabox = (databoxId: string) => {
    if (lockedDataboxIdSet.has(String(databoxId))) {
      return
    }
    const currentItems =
      selectedDocumentsByDatabox[databoxId]?.items ||
      selectedDocumentsByDatabox[String(databoxId)]?.items ||
      []
    if (currentItems.some((item: any) => isLockedItem(item, lockedItemIdSet))) {
      return
    }
    // Recorded so the pre-select/reconstruction effect can re-apply this removal if it's still
    // in flight (or runs again) after this call.
    pendingRemovedDataboxIdsRef.current.add(databoxId)
    setHasPendingRemoval(true)

    setSelectedRows((prev) => prev.filter((id) => id !== databoxId))
    setSelectedDocumentsByDatabox((prev) => {
      const { [databoxId]: _, ...rest } = prev
      return rest
    })
    setDataTableKey((k) => k + 1)
  }

  // Toggles a databox's whole-row selection when the row is clicked anywhere
  const toggleDataboxSelection = (row: Databox) => {
    // Empty databoxes (0 files and 0 data products) can't be selected - nothing to add
    if (row.databoxDocumentsCount === 0 && row.databoxDataProductsCount === 0) return
    const rowIdStr = String(row.id)
    const rowSmartDocsIdStr = (row as any).smartDocsDataboxId
      ? String((row as any).smartDocsDataboxId)
      : null
    if (
      lockedDataboxIdSet.has(rowIdStr) ||
      (rowSmartDocsIdStr && lockedDataboxIdSet.has(rowSmartDocsIdStr))
    ) {
      return
    }
    const isInSelectedRows = selectedRows.some((id) => {
      const selectedIdStr = String(id)
      return (
        selectedIdStr === rowIdStr || (rowSmartDocsIdStr && selectedIdStr === rowSmartDocsIdStr)
      )
    })
    const hasDocumentSelection =
      (selectedDocumentsByDatabox[row.id]?.items?.length ?? 0) > 0 ||
      (selectedDocumentsByDatabox[rowIdStr]?.items?.length ?? 0) > 0 ||
      (rowSmartDocsIdStr
        ? (selectedDocumentsByDatabox[rowSmartDocsIdStr]?.items?.length ?? 0) > 0
        : false)

    if (isInSelectedRows || hasDocumentSelection) {
      const currentItems =
        selectedDocumentsByDatabox[row.id]?.items ||
        selectedDocumentsByDatabox[rowIdStr]?.items ||
        (rowSmartDocsIdStr ? selectedDocumentsByDatabox[rowSmartDocsIdStr]?.items || [] : [])
      if (currentItems.some((item) => isLockedItem(item, lockedItemIdSet))) {
        return
      }
      // Uncheck entirely - clears both the whole-row selection and any narrowed
      // file/product selection for this databox
      setSelectedRows((prev) =>
        prev.filter((id) => String(id) !== rowIdStr && String(id) !== rowSmartDocsIdStr)
      )
      setSelectedDocumentsByDatabox((prev) => {
        const next = { ...prev }
        delete next[row.id]
        delete next[rowIdStr]
        if (rowSmartDocsIdStr) delete next[rowSmartDocsIdStr]
        return next
      })
    } else {
      setSelectedRows((prev) => [...prev, row.id])
    }
  }

  useImperativeHandle(ref, () => ({
    uncheckDatabox: handleRemoveDatabox,
    removeItem: handleRemoveItem,
    commitPendingSelection: () => addDataboxes({ shouldCloseModal: false })
  }))

  const onSelectionChange = (selectedItems: any[]) => {
    if (!browsingDataboxId) return

    const browsingDatabox = databoxes.find((db) => String(db.id) === String(browsingDataboxId))
    // Use consistent key: prefer smartDocsDataboxId, otherwise use stringified id
    const databoxKey = browsingDatabox?.smartDocsDataboxId
      ? String(browsingDatabox.smartDocsDataboxId)
      : String(browsingDataboxId)
    const databoxName = browsingDatabox?.name ?? ''

    setSelectedDocumentsByDatabox((prev) => {
      // Remove all possible key variations for this databox to prevent duplicates
      const cleaned = { ...prev }
      delete cleaned[browsingDataboxId]
      delete cleaned[String(browsingDataboxId)]
      if (browsingDatabox?.smartDocsDataboxId) {
        delete cleaned[browsingDatabox.smartDocsDataboxId]
        delete cleaned[String(browsingDatabox.smartDocsDataboxId)]
      }

      // Add selection if any items selected
      if (selectedItems.length > 0) {
        cleaned[databoxKey] = { name: databoxName, items: selectedItems }
      }

      return cleaned
    })

    // Remove whole-box selection when custom items are picked - check both id forms since
    // selectedRows uses plain id while databoxKey may be the smartDocsDataboxId. When the
    // custom selection is cleared (empty array), fall back to whole-databox selected instead
    // of leaving the databox completely unselected.
    if (selectedItems.length > 0) {
      setSelectedRows((prev) =>
        prev.filter((id) => String(id) !== String(browsingDataboxId) && String(id) !== databoxKey)
      )
    } else {
      setSelectedRows((prev) =>
        prev.some((id) => String(id) === String(browsingDataboxId))
          ? prev
          : [...prev, browsingDataboxId]
      )
    }
  }

  // Memoized so DataboxDataGrid (React.memo'd, can render 100+ rows) only gets new prop
  // references when the browsed databox's actual selection changes - not on every unrelated
  // re-render
  const browsingSelection = useMemo(() => {
    if (!browsingDataboxId) return null
    const browsingIdStr = String(browsingDataboxId)
    const browsingDatabox = databoxes.find((db) => String(db.id) === browsingIdStr)
    const smartDocsIdStr = browsingDatabox?.smartDocsDataboxId
      ? String(browsingDatabox.smartDocsDataboxId)
      : null

    return (
      selectedDocumentsByDatabox[browsingDataboxId] ||
      selectedDocumentsByDatabox[browsingIdStr] ||
      (smartDocsIdStr ? selectedDocumentsByDatabox[smartDocsIdStr] : null) ||
      null
    )
  }, [browsingDataboxId, databoxes, selectedDocumentsByDatabox])

  const browsingSelectedItemIds = useMemo(
    () => (browsingSelection?.items ?? []).map((i: any) => i.id),
    [browsingSelection]
  )
  const browsingPreSelectedItems = useMemo(
    () => browsingSelection?.items ?? [],
    [browsingSelection]
  )

  // `shouldCloseModal` defaults to true; set to false when called via commitPendingSelection
  // (the other tab's submit is deciding whether to close, not this one)
  async function addDataboxes(options?: { shouldCloseModal?: boolean }) {
    const shouldCloseModal = options?.shouldCloseModal ?? true

    // Include whole-box selections AND databoxes with specific file/product selections
    const allSelectedIds = new Set([
      ...selectedRows.map((id) => String(id)),
      ...Object.keys(selectedDocumentsByDatabox)
    ])

    // Only include databoxes that are currently selected (checked)
    // This handles both additions and removals (unchecked items won't be included)
    const selectedDataboxes = databoxes.filter((db) => {
      const dbIdStr = String(db.id)
      const dbSmartDocsIdStr = (db as any).smartDocsDataboxId
        ? String((db as any).smartDocsDataboxId)
        : null
      return (
        allSelectedIds.has(dbIdStr) || (dbSmartDocsIdStr && allSelectedIds.has(dbSmartDocsIdStr))
      )
    })

    if (selectedDataboxes.length === 0) {
      // Still detect/apply removals if existing databoxes got fully unchecked
      if (!initialDataboxId && removeDataboxFromAgent) {
        const removedIds: string[] = []

        if (existingDataboxes.length > 0) {
          const existingIds = existingDataboxes
            .filter((db: any) => db.scope !== 'Personal')
            .map((db: any) => String(db.smartDocsDataboxId || db.id))
          removedIds.push(...existingIds.filter((id: string) => !selectedRows.includes(id)))
        }

        // This early-return path never calls addDataboxToAgent, so a Personal Databox "Remove all" made from this tab's panel must be detected and persisted here too.
        const existingPersonalDatabox = existingDataboxes.find((db: any) => db.scope === 'Personal')
        const livePersonalStillHasItems = existingPersonalDataboxForPanel.some(
          (db: any) => (db?.selectedItems?.length ?? 0) > 0
        )
        if (existingPersonalDatabox && !livePersonalStillHasItems) {
          removedIds.push(
            String(existingPersonalDatabox.smartDocsDataboxId || existingPersonalDatabox.id)
          )
        }

        if (removedIds.length > 0) {
          removeDataboxFromAgent(removedIds)
        }
      }
      if (shouldCloseModal) toggle()
      return
    }

    try {
      setShowSpinner(true)

      // Fetch full databox details (including smartDocsDataboxId) for each selected databox
      const databoxDetailsPromises = selectedDataboxes.map((db) =>
        getDataboxInfo(db.id, false).catch((err) => {
          console.error(`Error fetching databox ${db.id}:`, err)
          return db // Fallback to existing data if fetch fails
        })
      )

      const fullDataboxDetails = await Promise.all(databoxDetailsPromises)

      // Map with selections and full details
      const newDataboxes = fullDataboxDetails.map((fullDb: any, index) => {
        const originalDb = selectedDataboxes[index]
        const dbIdStr = String(originalDb.id)
        const dbSmartDocsIdStr = fullDb?.smartDocsDataboxId
          ? String(fullDb.smartDocsDataboxId)
          : null

        const selections =
          selectedDocumentsByDatabox[originalDb.id] ||
          selectedDocumentsByDatabox[dbIdStr] ||
          (dbSmartDocsIdStr ? selectedDocumentsByDatabox[dbSmartDocsIdStr] : null)

        // Transform selectedItems into customSelection format
        let customSelection = null
        if (selections?.items && selections.items.length > 0) {
          const cognitiveSearchIds: string[] = []
          const dataProductsIds: Array<{ dataProductId: string; databoxId: string }> = []

          selections.items.forEach((item: any) => {
            if (item.type === 'file') {
              const cognitiveSearchId = item.cognitiveSearchId || item.externalId || item.id
              if (cognitiveSearchId) {
                cognitiveSearchIds.push(String(cognitiveSearchId))
              }
            } else if (item.type === 'dataproduct') {
              // Persist the product's real id (dataProductId), not the row's display/selection
              // id (which may be an associationId) - the real id is what getDataProduct needs
              // to successfully re-fetch this product later.
              dataProductsIds.push({
                dataProductId: String(item.dataProductId ?? item.id),
                databoxId: dbSmartDocsIdStr || dbIdStr
              })
            }
          })

          customSelection = {
            ...(cognitiveSearchIds.length > 0 && { cognitiveSearchIds }),
            ...(dataProductsIds.length > 0 && { dataProductsIds })
          }
        }

        const databoxWithDetails = {
          ...fullDb, // Full details including smartDocsDataboxId
          ...(customSelection && { customSelection }), // Save IDs for backend
          teamIdentifier: selectedTeam?.smartDocsTeamId || originalDb.teamIdentifier // Always prefer smartDocsTeamId
        }

        // Keep selectedItems for display components (full objects)
        if (selections?.items && selections.items.length > 0) {
          databoxWithDetails.selectedItems = selections.items
        } else {
          // Whole databox - no custom selection
          delete databoxWithDetails.selectedItems
          delete databoxWithDetails.customSelection
        }

        return databoxWithDetails
      })

      // Detect removed databoxes (if any) - only in agent context
      if (!initialDataboxId) {
        const existingIds = existingDataboxes
          .filter((db: any) => db.scope !== 'Personal')
          .map((db: any) => String(db.smartDocsDataboxId || db.id))

        // Find databoxes that were removed (in existing but not in selected)
        const removedIds = existingIds.filter(
          (id: string) => !selectedRows.includes(id) && !lockedDataboxIdSet.has(String(id))
        )

        // The Personal Databox is tracked separately, so an emptied-out selection must be explicitly detected and removed here too, since addDataboxToAgent below never removes entries that are simply absent from its list.
        const existingPersonalDatabox = existingDataboxes.find((db: any) => db.scope === 'Personal')
        const livePersonalStillHasItems = existingPersonalDataboxForPanel.some(
          (db: any) => (db?.selectedItems?.length ?? 0) > 0
        )
        if (existingPersonalDatabox && !livePersonalStillHasItems) {
          removedIds.push(
            String(existingPersonalDatabox.smartDocsDataboxId || existingPersonalDatabox.id)
          )
        }

        // Remove unchecked databoxes
        if (removedIds.length > 0 && removeDataboxFromAgent) {
          removeDataboxFromAgent(removedIds)
        }
      }

      // Preserve Personal Databox tab selections
      const personalDataboxes = existingPersonalDataboxForPanel
        .filter((db: any) => (db?.selectedItems?.length ?? 0) > 0)
        .map((db: any) => ({ ...db, scope: 'Personal' }))
      addDataboxToAgent?.([...personalDataboxes, ...newDataboxes])
      if (shouldCloseModal) toggle()
    } catch (error) {
      console.error('Error fetching databox details:', error)
      toast.error('Failed to add databoxes. Please try again.')
    } finally {
      setShowSpinner(false)
    }
  }

  // Memoized (stable identity) - an unstable `columns` reference here previously caused a
  // "Too many re-renders" loop in react-data-table-component's pagination fix-up logic.

  const teamColumns: any[] = useMemo(
    () => [
      {
        name: '',
        selector: (row: any) => row.name,
        sortable: true,
        width: '50px',
        cell: (row: any) => {
          const isDisabled = row.status === 'PendingAdminApproval'
          return (
            <div
              className={isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
              onClick={() => !isDisabled && handleBrowseTeam(row)}>
              <Avatar
                content={
                  row?.teamIconUrl
                    ? appendToken(row.teamIconUrl)
                    : getInitials(row?.name || '-').slice(0, 2)
                }
                size="30px"
                shape="rounded"
                className={`min-w-[30px] border border-[var(--team-active-border)] flex justify-center items-center ${
                  isDisabled ? '!cursor-not-allowed' : ''
                }`}
              />
            </div>
          )
        }
      },
      {
        name: 'Team',
        selector: (row: any, rowIndex?: number) => row.name,
        sortable: true,
        width: '300px',
        cell: (row: any) => {
          const isDisabled = row.status === 'PendingAdminApproval'
          return (
            <div
              className={`flex align-items-center ml-2 ${
                isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              onClick={() => !isDisabled && handleBrowseTeam(row)}>
              <span className="d-flex align-items-center gap-1">
                {row.status === 'pending' && <PendingIcon size={16} />}
                <div className={`card-header-text team-name-text transition-colors`}>
                  {row.name}
                </div>
              </span>
            </div>
          )
        }
      },
      {
        name: 'Role',
        selector: (row: any, rowIndex?: number) => row.userRole ?? '-',
        sortable: true,
        width: '150px',
        cell: (row: any) => {
          const isDisabled = row.status === 'PendingAdminApproval'
          return (
            <div
              className={isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
              onClick={() => !isDisabled && handleBrowseTeam(row)}>
              {row.userRole ? (
                <Badge
                  color={
                    row.userRole === 'Owner'
                      ? 'var(--role-owner-bg)'
                      : row.userRole === 'Contributor'
                      ? 'var(--role-contributor-bg)'
                      : 'var(--role-member-bg)'
                  }
                  className={isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                  label={row.userRole}
                />
              ) : (
                <span
                  style={
                    isDisabled ? { color: 'var(--text-subheading)', cursor: 'not-allowed' } : {}
                  }>
                  -
                </span>
              )}
            </div>
          )
        }
      },
      {
        name: 'Databoxes',
        //   selector: (row: any, rowIndex?: number) => row.tags.join(', '),
        sortable: false,
        width: '150px',
        cell: (row: any) => {
          const isDisabled = row.status === 'PendingAdminApproval'
          return (
            <div
              className={`d-flex flex-wrap gap-1 ${
                isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              onClick={() => !isDisabled && handleBrowseTeam(row)}>
              {row.totalDataboxesCount ? row.totalDataboxesCount : '-'}
            </div>
          )
        }
      },
      {
        name: 'Owner(s)',
        selector: (row: any) =>
          Array.isArray(row.owners) ? row.owners.map((o: any) => o.displayName).join(', ') : '',
        sortable: false,
        width: '300px',
        cell: (row: any) => {
          const isDisabled = row.status === 'PendingAdminApproval'
          const clickableClass = isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
          const onOwnerCellClick = () => !isDisabled && handleBrowseTeam(row)
          return Array.isArray(row.owners) && row.owners.length === 1 ? (
            <div className={`flex items-center ${clickableClass}`} onClick={onOwnerCellClick}>
              {row.owners[0].profilePictureBase64Encoded ? (
                <img
                  src={row.owners[0].profilePictureBase64Encoded}
                  alt={row.owners[0].displayName}
                  className="w-[32px] h-[32px] object-cover rounded-full border theme-border-default mr-2"
                />
              ) : (
                <div className="w-8 h-8 rounded-full dsm-avatar-brand flex items-center justify-center text-xs font-bold mr-2">
                  {getInitials(row.owners[0].displayName)}
                </div>
              )}
              <div className="flex flex-col">
                <span className="subtitle-text theme-text-heading">
                  {row.owners[0].displayName}
                </span>
                <span className="caption-text theme-text-subheading">{row.owners[0].jobTitle}</span>
              </div>
            </div>
          ) : (
            <div
              className={`flex -space-x-2 items-center ${clickableClass}`}
              onClick={onOwnerCellClick}>
              {Array.isArray(row.owners) &&
                row.owners.slice(0, 4).map((owner: any) => {
                  const hasProfilePic = !!owner.profilePictureBase64Encoded
                  const initials = owner.displayName
                    ? owner.displayName
                        .split(' ')
                        .map((word: string) => word[0])
                        .join('')
                        .toUpperCase()
                    : '-'
                  return hasProfilePic ? (
                    <span key={owner.id}>
                      <img
                        src={owner.profilePictureBase64Encoded}
                        alt={owner.displayName}
                        className="w-[32px] h-[32px] object-cover rounded-full dsm-avatar-stack-border"
                        id={`owner-${row.id}-${owner.id}`}
                      />
                      <UncontrolledTooltip
                        target={`owner-${row.id}-${owner.id}`}
                        placement="bottom">
                        {owner.displayName}
                        {owner.jobTitle && <div>{owner.jobTitle}</div>}
                      </UncontrolledTooltip>
                    </span>
                  ) : (
                    <div
                      id={`owner-${row.id}-${owner.id}`}
                      key={owner.id}
                      className="w-8 h-8 rounded-full dsm-avatar-brand dsm-avatar-stack-border flex items-center justify-center text-xs font-bold">
                      {initials}
                      <UncontrolledTooltip
                        target={`owner-${row.id}-${owner.id}`}
                        placement="bottom">
                        {owner.displayName}
                      </UncontrolledTooltip>
                    </div>
                  )
                })}
              {(!Array.isArray(row.owners) || row.owners.length === 0) && (
                <div className="w-8 h-8 rounded-full theme-surface-muted flex items-center justify-center theme-text-subheading text-xs font-bold">
                  -
                </div>
              )}
            </div>
          )
        }
      }
    ],
    []
  )

  const getTeamDataboxes = async (teamId: string, smartDocsTeamId?: string) => {
    if (!teamId) return
    try {
      const response = await getDataboxes(teamId)
      if (response?.items) {
        const databoxes = response?.items
          ?.filter((databox: Databox) =>
            databox?.status ? databox?.status === 'Provisioned' : true
          )
          .map((db: any) => {
            return {
              ...db,
              type: 'databox',
              // Never fall back to the raw team GUID (teamId) here - teamIdentifier must be the
              // numeric smartDocsTeamId
              teamIdentifier: smartDocsTeamId || db.teamId || '',
              scopeId: db.scopeId || teamId // Use API's scopeId or fallback to teamId
            }
          })
        setDataboxes(databoxes || [])
      } else {
        toast.error('Failed to fetch databoxes')
      }
    } catch (e) {
      console.error('❌ Error fetching databoxes:', e)
      setDataboxes([])
    }
  }

  // Handle browse team databoxes
  const handleBrowseTeam = (team: any) => {
    // Selections don't carry over across teams - reset if switching to a different team than
    // the one last browsed (compare against the ref since "Back to Teams" nulls selectedTeam)
    if (lastBrowsedTeamIdRef.current && lastBrowsedTeamIdRef.current !== team.id) {
      setSelectedRows([])
      setSelectedDocumentsByDatabox({})
      setDataTableKey((k) => k + 1)
    }
    setSelectedTeam(team)
    // Pass both GUID (for API) and numeric smartDocsTeamId (for teamIdentifier)
    getTeamDataboxes(team.id, team.smartDocsTeamId)
  }

  // Memoized (stable identity) for the same reason as teamColumns above.

  const columns = useMemo(
    () => [
      {
        name: '',
        width: '35px',
        cell: (row: Databox) => {
          const isEmptyDatabox =
            row.databoxDocumentsCount === 0 && row.databoxDataProductsCount === 0
          const rowIdStr = String(row.id)
          const rowSmartDocsIdStr = (row as any).smartDocsDataboxId
            ? String((row as any).smartDocsDataboxId)
            : null
          const isInSelectedRows = selectedRows.some((id) => {
            const selectedIdStr = String(id)
            return (
              selectedIdStr === rowIdStr ||
              (rowSmartDocsIdStr && selectedIdStr === rowSmartDocsIdStr)
            )
          })
          const isChecked = isInSelectedRows || rowHasCustomSelection(row.id)
          const isLocked = lockedDataboxIdSet.has(String(row.smartDocsDataboxId))
          return (
            <div
              className={isEmptyDatabox || isLocked ? 'cursor-not-allowed' : undefined}
              title={
                isEmptyDatabox
                  ? 'Databox is empty'
                  : isLocked
                  ? 'Agent-level databox cannot be removed'
                  : undefined
              }>
              <BootstrapCheckbox
                name={`select-row-${row.id}`}
                checked={isChecked}
                disabled={isEmptyDatabox || isLocked}
                isPartiallySelected={rowHasCustomSelection(row.id)}
                wrapperClassName="databox-select-checkbox"
                onChange={() => toggleDataboxSelection(row)}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                data-testid={`checkbox-${row.id}`}
              />
            </div>
          )
        }
      },
      {
        name: 'DATABOX NAME',
        selector: (row: Databox) => row.name,
        sortable: true,
        cell: (row: Databox) => {
          const isEmptyDatabox =
            row.databoxDocumentsCount === 0 && row.databoxDataProductsCount === 0
          const canBrowse = !(loadingBrowseId === row.id || isEmptyDatabox)
          const openDatabox = () => {
            if (canBrowse) handleBrowseDatabox(row)
          }
          return (
            <div
              className={`flex items-center gap-1 py-1 w-full databox-name-cell ${
                canBrowse
                  ? 'cursor-pointer'
                  : isEmptyDatabox
                  ? 'cursor-not-allowed'
                  : 'cursor-default'
              }`}
              title={isEmptyDatabox ? 'Databox is empty' : undefined}
              onClick={openDatabox}>
              <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0">
                <IconBox size={24} color="var(--team-agent)" className="text-info" />
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-1">
                <div className="flex-1 min-w-0">
                  <div
                    className="font-medium theme-text-heading line-clamp-1 ellipsis databox-name-text"
                    title={row.name}>
                    {row.name}
                  </div>
                  <div
                    className="text-sm theme-text-subheading line-clamp-1 ellipsis"
                    title={row.description || ''}>
                    {row.description}
                  </div>
                </div>
                {loadingBrowseId === row.id ? (
                  <Spinner size="sm" className="flex-shrink-0" />
                ) : (
                  <IconChevronRight
                    size={18}
                    className={`databox-row-chevron flex-shrink-0 ${
                      canBrowse
                        ? 'theme-text-subheading cursor-pointer'
                        : 'opacity-30 cursor-not-allowed'
                    }`}
                  />
                )}
              </div>
            </div>
          )
        },
        width: '300px'
      },
      {
        name: 'FILES',
        selector: (row: Databox) => row.databoxDocumentsCount,
        cell: (row: Databox) => {
          const isEmptyDatabox =
            row.databoxDocumentsCount === 0 && row.databoxDataProductsCount === 0
          return (
            <div
              className={`w-full ${isEmptyDatabox ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              title={isEmptyDatabox ? 'Databox is empty' : undefined}
              onClick={() => !isEmptyDatabox && toggleDataboxSelection(row)}>
              <div className="font-medium theme-text-heading">
                {row?.databoxDocumentsCount || row?.filesCount || '0'}
              </div>
              <div className="text-sm theme-text-subheading">
                Files size: {row?.totalDocumentsSize || '-'}
              </div>
            </div>
          )
        },
        width: '200px'
      },
      {
        name: 'DATA PRODUCTS',
        selector: (row: Databox) => row.databoxDataProductsCount,
        cell: (row: Databox) => {
          const isEmptyDatabox =
            row.databoxDocumentsCount === 0 && row.databoxDataProductsCount === 0
          return (
            <div
              className={`w-full ${isEmptyDatabox ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              title={isEmptyDatabox ? 'Databox is empty' : undefined}
              onClick={() => !isEmptyDatabox && toggleDataboxSelection(row)}>
              <div className="font-medium theme-text-heading">
                {row?.databoxDataProductsCount || row?.dataProductsCount || '0'}
              </div>
              <div className="text-sm theme-text-subheading">
                Source: {row?.dataProductsMetadataSources || '-'}
              </div>
            </div>
          )
        },
        width: '200px'
      },
      {
        name: 'CREATED BY',
        selector: (row: Databox) => row.createdBy?.displayName,
        sortable: true,
        cell: (row: Databox) => {
          const isEmptyDatabox =
            row.databoxDocumentsCount === 0 && row.databoxDataProductsCount === 0
          return (
            <div
              className={`w-full ${isEmptyDatabox ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              title={isEmptyDatabox ? 'Databox is empty' : undefined}
              onClick={() => !isEmptyDatabox && toggleDataboxSelection(row)}>
              <div className="font-medium theme-text-heading">{row.createdBy?.displayName}</div>
              <div className="text-sm theme-text-subheading">{formatDate(row.createdAt)}</div>
            </div>
          )
        },
        width: '150px'
      },
      {
        name: 'LAST MODIFIED',
        selector: (row: Databox) => row.modifiedAt,
        sortable: true,
        cell: (row: Databox) => {
          const isEmptyDatabox =
            row.databoxDocumentsCount === 0 && row.databoxDataProductsCount === 0
          return (
            <div
              className={`w-full ${isEmptyDatabox ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              title={isEmptyDatabox ? 'Databox is empty' : undefined}
              onClick={() => !isEmptyDatabox && toggleDataboxSelection(row)}>
              <div className="font-medium theme-text-heading">
                {formatDate(row?.modifiedAt || row?.lastModifiedAt || '')}
              </div>
              <div className="text-sm text-theme-subheading">
                {(row.modifiedBy?.displayName || row?.lastModifiedBy?.displayName) &&
                  `by ${row.modifiedBy?.displayName || row?.lastModifiedBy?.displayName}`}
              </div>
            </div>
          )
        },
        width: '150px'
      }
    ],
    [selectedRows, selectedDocumentsByDatabox, loadingBrowseId, databoxes]
  )

  // A databox has only some of its files/data products selected (not the whole databox) -
  // used to show an indeterminate (partial) checkbox for it instead of a full checkmark
  const rowHasCustomSelection = (rowId: string) => {
    const row = databoxes.find((db) => String(db.id) === String(rowId))
    const smartDocsIdStr = row?.smartDocsDataboxId ? String(row.smartDocsDataboxId) : null
    return (
      (selectedDocumentsByDatabox[rowId]?.items?.length ?? 0) > 0 ||
      (smartDocsIdStr
        ? (selectedDocumentsByDatabox[smartDocsIdStr]?.items?.length ?? 0) > 0
        : false)
    )
  }

  // When modal opens for editing, directly load the specific databox's files/data products
  useEffect(() => {
    if (!initialDataboxId || !isOpen || browsingDataboxId) return

    const loadDataboxForEditing = async () => {
      setShowSpinner(true)
      try {
        const response = await getDataboxDetails(initialDataboxId)

        if (!response) {
          toast.error('Failed to fetch databox details')
          return
        }

        // Set browse view state
        setBrowsingDataboxId(initialDataboxId)
        setDataboxDetails(response)
        setDataboxFiles(response.files || [])
        setDataboxDataProducts(response.dataProducts || [])

        // Load previously selected items from selectedItems array
        const existingDatabox = existingDataboxes.find(
          (db: any) =>
            String(db.id) === String(initialDataboxId) ||
            String(db.smartDocsDataboxId) === String(initialDataboxId)
        )

        if (existingDatabox) {
          const key = response.smartDocsDataboxId || initialDataboxId
          let cognitiveSearchIds: string[] = []
          let dataProductsIds: string[] = []

          // Check for selectedItems array (current UI state)
          if (
            existingDatabox.selectedItems &&
            Array.isArray(existingDatabox.selectedItems) &&
            existingDatabox.selectedItems.length > 0
          ) {
            cognitiveSearchIds = [
              ...new Set<string>(
                existingDatabox.selectedItems
                  .filter((item: any) => item.cognitiveSearchId && item.type === 'file')
                  .map((item: any) => item.cognitiveSearchId)
              )
            ]

            dataProductsIds = [
              ...new Set<string>(
                existingDatabox.selectedItems
                  .filter((item: any) => item.type === 'dataproduct' && item.id)
                  .map((item: any) => String(item.id))
              )
            ]
          }

          // Fetch full objects from IDs
          if (cognitiveSearchIds.length > 0 || dataProductsIds.length > 0) {
            const items = await reconstructItemsFromIds(cognitiveSearchIds, dataProductsIds)
            setSelectedDocumentsByDatabox({
              [key]: { name: response.name || 'Databox', items }
            })
          }
        }

        // Cache details for right panel
        const filesCount = response.filesCount ?? response.files?.length ?? 0
        const dataProductsCount = response.dataProductsCount ?? response.dataProducts?.length ?? 0

        setDataboxDetailsCache({
          [initialDataboxId]: {
            files: response.files || [],
            dataProducts: response.dataProducts || [],
            filesCount,
            dataProductsCount
          }
        })

        // Set minimal databox info for breadcrumb/UI
        const databoxInfo = {
          id: initialDataboxId,
          smartDocsDataboxId: response.smartDocsDataboxId,
          name: response.name || 'Databox',
          description: response.description,
          databoxDocumentsCount: filesCount,
          databoxDataProductsCount: dataProductsCount,
          filesCount,
          dataProductsCount,
          createdBy: response.createdBy,
          createdAt: response.createdAt,
          modifiedAt: response.modifiedAt || response.lastModifiedAt,
          modifiedBy: response.modifiedBy || response.lastModifiedBy,
          lastModifiedAt: response.lastModifiedAt,
          lastModifiedBy: response.lastModifiedBy,
          dataProductsMetadataSources: response.dataProductsMetadataSources || [],
          totalDocumentsSize: response.totalDocumentsSize || 0,
          type: 'databox',
          status: response.status || 'Provisioned'
        }
        setDataboxes([databoxInfo])
      } catch (error) {
        console.error('Error fetching databox details for editing:', error)
        toast.error('Failed to load databox for editing')
      } finally {
        setShowSpinner(false)
      }
    }

    loadDataboxForEditing()
  }, [initialDataboxId, isOpen, browsingDataboxId])

  // The Personal Databox tab's current selection - only surface it here if it actually has
  // files selected.
  const personalDatabox = existingPersonalDataboxForPanel.find(
    (db: any) => (db?.selectedItems?.length ?? 0) > 0
  )

  const bodyContent = (
    <ModalBody className="p-1 flex-1 theme-surface overflow-hidden flex flex-col min-h-0">
      {/* Main Content Split */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Left Panel */}
        <div className="flex flex-col flex-1 overflow-hidden pr-2 min-h-0">
          {tabsBar}
          {selectedTeam || browsingDataboxId || isTeamLocked ? (
            // Show databox view when a team is selected OR in browse/edit mode OR the team is locked
            browsingDataboxId ? (
              <div className="flex flex-col h-full min-h-0">
                {/* Breadcrumb to navigate back to the databox list - hidden in edit mode */}
                {!initialDataboxId && (
                  <div className="flex">
                    {!isTeamLocked && !isEnterpriseTypeAgent && (
                      <div
                        className="flex items-center theme-text-subheading cursor-pointer hover:text-[var(--text-body)]"
                        onClick={() => {
                          setSelectedTeam(null)
                          setBrowsingDataboxId(null)
                          setDataTableKey((k) => k + 1)
                        }}>
                        <IconUsers size={16} className="mx-[8px]" />
                        Teams
                        <IconChevronRight size={16} />
                      </div>
                    )}
                    <div
                      className="flex items-center theme-text-subheading cursor-pointer hover:text-[var(--text-body)]"
                      onClick={() => {
                        setBrowsingDataboxId(null)
                        setDataTableKey((k) => k + 1)
                      }}>
                      {isEnterpriseTypeAgent && <IconUsers size={16} className="mx-[8px]" />}
                      <span className={isEnterpriseTypeAgent ? '' : 'ml-[8px]'}>
                        {isEnterpriseTypeAgent ? 'Databoxes' : selectedTeam?.name || 'Team'}
                      </span>
                      <IconChevronRight size={16} />
                    </div>
                    <div className="ml-[8px] theme-text-heading font-medium">
                      {databoxDetails?.name || 'Unnamed databox'}
                    </div>
                  </div>
                )}

                {/* Browse View - DataboxDataGrid */}
                <div className="flex-1 overflow-auto max-h-[calc(100vh-100px)] min-h-0">
                  <DataboxDataGrid
                    databoxId={browsingDataboxId ?? undefined}
                    smartDocsDataboxId={databoxDetails?.smartDocsDataboxId}
                    databoxFiles={databoxFiles}
                    databoxDataProducts={databoxDataProducts}
                    isFetchingDataboxDetails={isFetchingDetails}
                    userRole="Contributor"
                    showDeleteButton={false}
                    readOnly={false}
                    selectedItemIds={browsingSelectedItemIds}
                    preSelectedItems={browsingPreSelectedItems}
                    lockedDataboxIds={browsingLockedDataboxIds}
                    databoxDetails={databoxDetailsCache}
                    onDeleteItem={noopHandler}
                    onBulkDelete={noopHandler}
                    onDownloadFile={noopHandler}
                    onUploadFiles={noopHandler}
                    onAddDataProducts={noopHandler}
                    onSelectionChange={onSelectionChange}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col h-full min-h-0">
                {/* Breadcrumb back to teams - Enterprise agents get a static "Databoxes" placeholder instead */}
                {isEnterpriseTypeAgent ? (
                  <div className="flex mb-2">
                    <div className="flex items-center theme-text-subheading">
                      <IconUsers size={16} className="mx-[8px]" />
                    </div>
                    <div className="ml-[8px] theme-text-heading font-medium">Databoxes</div>
                  </div>
                ) : (
                  !isTeamLocked && (
                    <div className="flex mb-2">
                      <div
                        className="flex items-center theme-text-subheading cursor-pointer hover:text-[var(--text-body)]"
                        onClick={() => setSelectedTeam(null)}>
                        <IconUsers size={16} className="mx-[8px]" />
                        Teams
                        <IconChevronRight size={16} />
                      </div>
                      <div className="ml-[8px] theme-text-heading font-medium">
                        {selectedTeam?.name || 'Databoxes'}
                      </div>
                    </div>
                  )
                )}

                {/* Search */}
                <div className="mb-1">
                  <InputGroup
                    addon={<IconSearch size={14} />}
                    addonPosition="start"
                    addonType="merged"
                    isDarkMode={isDarkTheme}
                    className="search-input theme-input-group w-full"
                    placeholder="Search for databoxes"
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSearchTerm(e.target.value)
                    }
                    data-testid="-team-databox-search"
                  />
                </div>

                {/* DataTable */}
                <div className="mb-1 flex-1 overflow-x-scroll pb-3 min-h-0">
                  <DataTable
                    key={dataTableKey}
                    className="list-table modal-table-pagination-static"
                    columns={columns}
                    data={filteredDataboxes}
                    highlightOnHover
                    pointerOnHover
                    fixedHeader
                    fixedHeaderScrollHeight="calc(100vh - 340px)"
                    paginationDefaultPage={1}
                    paginationRowsPerPageOptions={[10, 15, 20, 25, 30, 50]}
                    paginationServerOptions={{
                      persistSelectedOnPageChange: false,
                      persistSelectedOnSort: false
                    }}
                    conditionalRowStyles={[
                      {
                        when: (row: Databox) =>
                          row.databoxDocumentsCount === 0 && row.databoxDataProductsCount === 0,
                        style: { cursor: 'not-allowed' }
                      }
                    ]}
                    pagination
                    paginationPerPage={10}
                    onChangePage={handleClientPaginationChange}
                    onChangeRowsPerPage={handleClientPaginationChange}
                    progressPending={showSpinner || isGettingDataboxList || isResolvingSource}
                    progressComponent={
                      <div className="w-full">
                        {[...Array(5)].map((_, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-1 p-1 border-b theme-border-default">
                            {/* Checkbox column */}
                            <div className="flex-shrink-0">
                              <Skeleton width={20} height={20} />
                            </div>
                            {/* Databox Name column */}
                            <div className="flex items-center gap-1 min-w-[300px]">
                              <Skeleton circle width={32} height={32} />
                              <div className="flex-1">
                                <Skeleton width="80%" height={16} />
                                <Skeleton width="60%" height={12} className="mt-[8px]" />
                              </div>
                            </div>
                            {/* Actions column */}
                            <div className="w-[150px]">
                              <Skeleton width={120} height={36} borderRadius={6} />
                            </div>
                            {/* Files column */}
                            <div className="w-[200px]">
                              <Skeleton width={40} height={16} />
                              <Skeleton width="70%" height={12} className="mt-[8px]" />
                            </div>
                            {/* Data Products column */}
                            <div className="w-[200px]">
                              <Skeleton width={40} height={16} />
                              <Skeleton width="80%" height={12} className="mt-[8px]" />
                            </div>
                            {/* Created By column */}
                            <div className="w-[150px]">
                              <Skeleton width="90%" height={16} />
                              <Skeleton width="70%" height={12} className="mt-[8px]" />
                            </div>
                            {/* Last Modified column */}
                            <div className="w-[150px]">
                              <Skeleton width="85%" height={16} />
                              <Skeleton width="75%" height={12} className="mt-[8px]" />
                            </div>
                          </div>
                        ))}
                      </div>
                    }
                    customStyles={{
                      table: {
                        style: {
                          backgroundColor: 'var(--surface-bg)',
                          color: 'var(--text-heading)'
                        }
                      },
                      header: {
                        style: {
                          display: 'none'
                        }
                      },
                      headRow: {
                        style: {
                          backgroundColor: 'var(--surface-muted)',
                          minHeight: `40px`
                        }
                      },
                      headCells: {
                        style: {
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: 'var(--text-subheading)',
                          paddingLeft: '16px',
                          paddingRight: '16px'
                        }
                      },
                      cells: {
                        style: {
                          paddingLeft: '16px',
                          paddingRight: '0px',
                          fontSize: '14px',
                          color: 'var(--text-body)'
                        }
                      },
                      rows: {
                        style: {
                          minHeight: '50px',
                          backgroundColor: 'var(--surface-bg)',
                          color: 'var(--text-heading)'
                        } as any,
                        highlightOnHoverStyle: {
                          backgroundColor: 'var(--item-hover-bg)',
                          color: 'var(--text-heading)',
                          cursor: 'pointer',
                          transitionDuration: '0.15s',
                          outline: 'none',
                          outlineStyle: 'none',
                          outlineWidth: 0,
                          borderBottomColor: 'var(--theme-border)'
                        } as any
                      },
                      progress: {
                        style: {
                          backgroundColor: 'var(--surface-bg)'
                        }
                      },
                      noData: {
                        style: {
                          backgroundColor: 'var(--surface-bg)',
                          color: 'var(--text-subheading)'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )
          ) : (
            // Show teams list when no team is selected
            <div className="flex flex-col h-full min-h-0">
              {/* Search */}
              <div className="mb-1">
                <InputGroup
                  addon={<IconSearch size={14} />}
                  addonPosition="start"
                  addonType="merged"
                  isDarkMode={isDarkTheme}
                  className="search-input theme-input-group w-full"
                  placeholder="Search for teams"
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchTerm(e.target.value)
                  }
                  data-testid="team-search"
                />
              </div>

              {/* Note: databox selections don't carry over between teams - hidden while the
                teams list is still loading (skeleton visible), and for Enterprise agents which
                only ever have the single Community team to choose from */}
              {!(isFetchingTeams || isResolvingSource) && !isEnterpriseTypeAgent && (
                <div className="theme-badge-danger flex items-start gap-50 text-sm rounded-lg p-1 mb-1">
                  <IconAlertTriangle size={20} className="flex-shrink-0 mt-0.5" />
                  <span className="font-bold text-base">
                    You can only select Databoxes from one Team. Switching Teams will clear your
                    current selections.
                  </span>
                </div>
              )}

              {/* Teams DataTable */}
              <div className="mb-1 flex-1 overflow-x-scroll pb-3 min-h-0">
                <DataTable
                  key={`teams-${dataTableKey}`}
                  className="list-table modal-table-pagination-static"
                  columns={teamColumns}
                  data={filteredTeams}
                  pagination
                  paginationPerPage={10}
                  onChangePage={handleClientPaginationChange}
                  onChangeRowsPerPage={handleClientPaginationChange}
                  paginationDefaultPage={1}
                  paginationRowsPerPageOptions={[10, 15, 20, 25, 30, 50]}
                  highlightOnHover
                  pointerOnHover
                  fixedHeader
                  fixedHeaderScrollHeight="calc(100vh - 340px)"
                  paginationServerOptions={{
                    persistSelectedOnPageChange: false,
                    persistSelectedOnSort: false
                  }}
                  onRowClicked={(row: any) => {
                    if (row.status !== 'PendingAdminApproval') handleBrowseTeam(row)
                  }}
                  conditionalRowStyles={[
                    {
                      when: (row: any) => row.status === 'PendingAdminApproval',
                      style: {
                        cursor: 'not-allowed',
                        opacity: 0.6
                      }
                    }
                  ]}
                  progressPending={isFetchingTeams || isResolvingSource}
                  progressComponent={
                    <div className="w-full">
                      {[...Array(5)].map((_, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-1 p-1 border-b theme-border-default">
                          {/* Avatar column - 50px */}
                          <div className="w-[50px] flex-shrink-0">
                            <Skeleton circle width={30} height={30} />
                          </div>
                          {/* Team name column - 300px */}
                          <div className="w-[300px]">
                            <Skeleton width="70%" height={14} />
                          </div>
                          {/* Role column - 150px */}
                          <div className="w-[150px]">
                            <Skeleton width={70} height={24} borderRadius={12} />
                          </div>
                          {/* Databoxes column - 150px */}
                          <div className="w-[150px]">
                            <Skeleton width={30} height={16} />
                          </div>
                          {/* Owner(s) column - 300px */}
                          <div className="w-[300px] flex items-center gap-2">
                            <Skeleton circle width={32} height={32} />
                            <div className="flex-1">
                              <Skeleton width="60%" height={14} />
                              <Skeleton width="40%" height={12} className="mt-1" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  }
                  customStyles={{
                    table: {
                      style: {
                        backgroundColor: 'var(--surface-bg)',
                        color: 'var(--text-heading)'
                      }
                    },
                    header: {
                      style: {
                        display: 'none'
                      }
                    },
                    headRow: {
                      style: {
                        backgroundColor: 'var(--surface-muted)'
                      }
                    },
                    headCells: {
                      style: {
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'var(--text-subheading)',
                        paddingLeft: '16px',
                        paddingRight: '16px'
                      }
                    },
                    cells: {
                      style: {
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        fontSize: '14px',
                        color: 'var(--text-body)'
                      }
                    },
                    rows: {
                      style: {
                        minHeight: '50px',
                        backgroundColor: 'var(--surface-bg)',
                        color: 'var(--text-heading)'
                      } as any,
                      highlightOnHoverStyle: {
                        backgroundColor: 'var(--item-hover-bg)',
                        color: 'var(--text-heading)',
                        cursor: 'pointer',
                        transitionDuration: '0.15s',
                        outline: 'none',
                        outlineStyle: 'none',
                        outlineWidth: 0,
                        borderBottomColor: 'var(--theme-border)'
                      } as any
                    },
                    progress: {
                      style: {
                        backgroundColor: 'var(--surface-bg)'
                      }
                    },
                    noData: {
                      style: {
                        backgroundColor: 'var(--surface-bg)',
                        color: 'var(--text-subheading)'
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Selected Sources */}
        <div className="flex flex-col overflow-auto pl-3 ml-1 border-l theme-border-default w-[35%] max-h-[calc(100vh-200px)]">
          <SelectedSourcesPanel
            selectedRows={selectedRows}
            selectedDocumentsByDatabox={
              personalDatabox
                ? {
                    ...selectedDocumentsByDatabox,
                    [personalDatabox.id]: {
                      name: personalDatabox.name,
                      items: personalDatabox.selectedItems ?? []
                    }
                  }
                : selectedDocumentsByDatabox
            }
            isFetchingTeam={
              isFetchingDetails ||
              isLoadingExistingSources ||
              // Own selection isn't reconstructed until the team databox list loads - avoids
              // showing this tab's panel with the existing team databox missing meanwhile.
              (existingDataboxIds.length > 0 && !hasInitializedExistingSelection)
            }
            isFetchingPersonal={isFetchingPersonal}
            isFetchingDataProducts={isFetchingDataProducts}
            databoxes={
              personalDatabox
                ? [
                    ...databoxes,
                    {
                      id: personalDatabox.id,
                      name: personalDatabox.name,
                      databoxDocumentsCount:
                        personalDatabox.filesCount ?? personalDatabox.selectedItems?.length ?? 0,
                      filesCount:
                        personalDatabox.filesCount ?? personalDatabox.selectedItems?.length ?? 0
                    }
                  ]
                : databoxes
            }
            databoxDetailsCache={databoxDetailsCache}
            initialDataboxId={initialDataboxId}
            personalDataboxId={personalDatabox?.id}
            lockedDataboxIds={Array.from(lockedDataboxIdSet)}
            lockedItemIds={Array.from(lockedItemIdSet)}
            lockedDataProductIds={Array.from(lockedDataProductIdSet)}
            // Removes an ENTIRE databox entry
            onRemoveDatabox={(databoxId) => {
              if (personalDatabox && String(databoxId) === String(personalDatabox.id)) {
                onRemovePersonalDatabox?.()
                return
              }
              handleRemoveDatabox(databoxId)
            }}
            // Removes a SINGLE file/data-product from within a databox's narrowed selection
            onRemoveItem={(databoxId, itemId) => {
              if (personalDatabox && String(databoxId) === String(personalDatabox.id)) {
                onRemovePersonalFile?.(itemId)
                return
              }
              handleRemoveItem(databoxId, itemId)
            }}
            standaloneDataProducts={standaloneDataProducts}
            onRemoveDataProduct={onRemoveDataProduct}
          />
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="flex items-center justify-between mt-2">
        <div className="flex gap-1">
          {selectedTeam && !browsingDataboxId && !isTeamLocked && !isEnterpriseTypeAgent && (
            <Button
              label="Back to Teams"
              color="primary"
              icon={<IconArrowLeft />}
              onClick={() => setSelectedTeam(null)}
              variant="outline"
              className="theme-accent-outline-btn"
            />
          )}
          {browsingDataboxId && !initialDataboxId && (
            <Button
              label="Back"
              color="primary"
              icon={<IconArrowLeft />}
              onClick={() => {
                setBrowsingDataboxId(null)
                setDataTableKey((k) => k + 1)
              }}
              variant="outline"
              className="theme-accent-outline-btn"
            />
          )}
        </div>
        <div className="flex gap-1">
          <Button
            label="Cancel"
            variant="outline"
            className="theme-neutral-outline-btn"
            onClick={toggle}
          />
          <Button
            label={
              existingDataboxes.length === 0 &&
              standaloneDataProducts.length === 0 &&
              !hasExistingDataProducts
                ? 'Add sources'
                : 'Update sources'
            }
            color="primary"
            onClick={() => {
              // Commit the Data Products tab's pending selection too
              onBeforeSubmit?.()
              addDataboxes()
            }}
            disabled={
              // Disable only if nothing is selected and there's nothing to remove - hasExistingDataProducts keeps this enabled long enough to commit a data product removal made from this tab's panel.
              selectedRows.length === 0 &&
              Object.keys(selectedDocumentsByDatabox).length === 0 &&
              standaloneDataProducts.length === 0 &&
              !hasExistingDataProducts &&
              existingDataboxes.length === 0
            }
          />
        </div>
      </div>
    </ModalBody>
  )

  if (asTabContent) {
    return bodyContent
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="xl"
      contentClassName="w-[90%] max-w-[2000px] max-h-[95vh] mx-auto flex flex-col">
      <ModalHeader
        className={`!justify-between !border-0 !flex !items-center chat-primary theme-bg flex-shrink-0`}
        toggle={toggle}
        close={
          <IconX
            size={20}
            onClick={toggle}
            className="text-white transition-opacity duration-300 ease-in-out hover:opacity-80 cursor-pointer"
            data-testid="close-button"
          />
        }>
        <div className="flex items-center gap-[8px]">
          <IconPackage size={20} className="text-white" />
          <span className="text-white">Add sources</span>
        </div>
      </ModalHeader>
      {bodyContent}
    </Modal>
  )
}

AddDataboxModal.displayName = 'AddDataboxModal'

export default AddDataboxModal
